import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { UserService } from "../../layout/service/user.service";
import { MessageService } from "primeng/api";
import { CommonModule } from "@angular/common";
import { FormsModule } from '@angular/forms';
import { TableModule } from "primeng/table";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { DialogModule } from "primeng/dialog";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { ToastModule } from "primeng/toast";
import { TagModule } from "primeng/tag";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ConfirmationService } from "primeng/api";
import { PdfViewerModule } from 'ng2-pdf-viewer';

@Component({
  selector: "app-list-anatomi",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    ProgressSpinnerModule,
    ToastModule,
    TagModule,
    ConfirmDialogModule,
    PdfViewerModule
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <div class="p-6 bg-white rounded-lg shadow-sm">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <div>
          <h1 class="text-2xl font-bold text-gray-800">Daftar Anatomi</h1>
          <p class="text-gray-600 mt-1">Kelola data anatomi yang tersedia</p>
        </div>
        <p-button
          label="Tambah Anatomi"
          icon="pi pi-plus"
          (onClick)="navigateToCreate()"
        ></p-button>
      </div>

      <!-- Data Table -->
      <p-table
        [value]="anatomyList"
        [loading]="loading"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[5, 10, 20]"
        [globalFilterFields]="['name', 'description']"
        responsiveLayout="scroll"
        styleClass="p-datatable-striped"
        [showCurrentPageReport]="true"
        currentPageReportTemplate="Menampilkan {first} sampai {last} dari {totalRecords} data"
      >
        <ng-template pTemplate="caption">
          <div class="flex justify-between items-center">
            <span class="p-input-icon-left">
              <i class="pi pi-search"></i>
              <input
                pInputText
                type="text"
                (input)="applyGlobalFilter($event)"
                placeholder="Cari anatomi..."
                class="w-72"
              />
            </span>
            <p-button
              icon="pi pi-refresh"
              severity="secondary"
              (onClick)="loadAnatomyList()"
              [loading]="loading"
            ></p-button>
          </div>
        </ng-template>

        <!-- Empty State -->
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="6" class="text-center py-8">
              <div class="flex flex-col items-center">
                <i class="pi pi-inbox text-4xl text-gray-400 mb-4"></i>
                <p class="text-gray-500 text-lg">Tidak ada data anatomi</p>
                <p class="text-gray-400 text-sm">Klik "Tambah Anatomi" untuk membuat data baru</p>
              </div>
            </td>
          </tr>
        </ng-template>

        <!-- Table Headers -->
        <ng-template pTemplate="header">
          <tr>
            <th class="w-16">No</th>
            <th>Nama Anatomi</th>
            <th>Deskripsi</th>
            <th class="w-24">Status</th>
            <th class="w-32">Jumlah Konten</th>
            <th class="w-40">Aksi</th>
          </tr>
        </ng-template>

        <!-- Table Body -->
        <ng-template pTemplate="body" let-anatomy let-rowIndex="rowIndex">
          <tr>
            <td>{{ rowIndex + 1 }}</td>
            <td>
              <div class="font-medium text-gray-900">{{ anatomy.name || 'Tidak ada nama' }}</div>
            </td>
            <td>
              <div class="max-w-xs truncate text-gray-600">
                {{ anatomy.description || 'Tidak ada deskripsi' }}
              </div>
            </td>
            <td>
              <p-tag
                [value]="anatomy.is_active ? 'Aktif' : 'Tidak Aktif'"
                [severity]="anatomy.is_active ? 'success' : 'danger'"
              ></p-tag>
            </td>
            <td class="text-center">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {{ anatomy.content_count || 0 }} konten
              </span>
            </td>
            <td>
              <div class="flex gap-2">
                <p-button
                  icon="pi pi-eye"
                  severity="info"
                  size="small"
                  pTooltip="Lihat Detail"
                  (onClick)="viewDetails(anatomy)"
                ></p-button>
                <p-button
                  icon="pi pi-pencil"
                  severity="warn"
                  size="small"
                  pTooltip="Edit"
                  (onClick)="editAnatomy(anatomy)"
                ></p-button>
                <p-button
                  icon="pi pi-trash"
                  severity="danger"
                  size="small"
                  pTooltip="Hapus"
                  (onClick)="deleteAnatomy(anatomy)"
                ></p-button>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>

      <!-- Detail Dialog -->
      <p-dialog
        header="Detail Anatomi"
        [(visible)]="showDetailDialog"
        [modal]="true"
        [style]="{ width: '80vw', height: '80vh' }"
        [maximizable]="true"
        [closable]="true"
      >
        <div *ngIf="selectedAnatomy" class="space-y-6">
          <!-- Basic Info -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <h3 class="text-lg font-semibold text-gray-800 mb-3">Informasi Dasar</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Nama</label>
                <p class="text-gray-900">{{ selectedAnatomy.name || 'Tidak ada nama' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <p-tag
                  [value]="selectedAnatomy.is_active ? 'Aktif' : 'Tidak Aktif'"
                  [severity]="selectedAnatomy.is_active ? 'success' : 'danger'"
                ></p-tag>
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">Deskripsi</label>
                <p class="text-gray-900">{{ selectedAnatomy.description || 'Tidak ada deskripsi' }}</p>
              </div>
            </div>
          </div>

          <!-- Content List -->
          <div *ngIf="anatomyDetails && anatomyDetails.length > 0">
            <h3 class="text-lg font-semibold text-gray-800 mb-3">Daftar Konten ({{ anatomyDetails.length }} item)</h3>
            <div class="space-y-4 max-h-96 overflow-y-auto">
              <div
                *ngFor="let content of anatomyDetails; let i = index"
                class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div class="flex justify-between items-start mb-2">
                  <h4 class="font-medium text-gray-900">Konten {{ i + 1 }}</h4>
                  <span class="text-xs text-gray-500">ID: {{ content.id }}</span>
                </div>

                <!-- Question -->
                <div *ngIf="content.question" class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Pertanyaan:</label>
                  <div class="text-sm text-gray-900 bg-blue-50 p-3 rounded border-l-4 border-blue-400" [innerHTML]="content.question">
                  </div>
                </div>

                <!-- Image -->
                <div *ngIf="content.image && !isPdfData(content.image)" class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Gambar:</label>
                  <div class="relative group inline-block">
                    <img
                      [src]="getImageSrc(content.image)"
                      alt="Gambar anatomi"
                      class="max-w-xs h-auto rounded border cursor-pointer hover:opacity-80 transition-opacity"
                      (click)="viewImageBase64(content.image)"
                      (error)="onImageError($event)">
                  </div>
                </div>

                <!-- Explanation -->
                <div *ngIf="content.explanation" class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Penjelasan:</label>

                  <!-- Check if explanation contains PDF data -->
                  <div *ngIf="isPdfData(content.explanation)" class="p-3 bg-blue-50 border border-blue-200 rounded">
                    <div class="flex items-center gap-2 mb-2">
                      <i class="pi pi-file-pdf text-red-500"></i>
                      <span class="text-sm text-blue-700 font-medium">Penjelasan berupa PDF</span>
                    </div>
                    <div class="flex gap-2">                    <p-button
                      label="Lihat PDF"
                      icon="pi pi-eye"
                      severity="success"
                      size="small"
                      (onClick)="viewPDFBase64(content.explanation)"
                    ></p-button>
                    <p-button
                      label="Buka di Tab Baru"
                      icon="pi pi-external-link"
                      severity="info"
                      size="small"
                      (onClick)="openPdfInNewTab(content.explanation)"
                    ></p-button>
                    </div>
                  </div>

                  <!-- Regular text explanation -->
                  <div *ngIf="!isPdfData(content.explanation)" class="text-sm text-gray-600 bg-gray-50 p-3 rounded" [innerHTML]="content.explanation"></div>
                </div>

                <!-- Answer Key -->
                <div *ngIf="content.key_answer" class="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                  <label class="block text-sm font-medium text-green-800 mb-1">Kunci Jawaban:</label>
                  <div class="text-sm text-green-700 font-medium">{{ content.key_answer }}</div>
                </div>

                <!-- PDF File -->
                <div *ngIf="content.pdf" class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                  <label class="block text-sm font-medium text-blue-800 mb-2">File PDF:</label>
                  <div class="flex items-center gap-2">
                    <i class="pi pi-file-pdf text-red-500"></i>
                    <span class="text-sm text-blue-700">Dokumen PDF</span>
                    <p-button
                      label="Lihat PDF"
                      icon="pi pi-eye"
                      severity="success"
                      size="small"
                      (onClick)="viewPDFBase64(content.pdf)"
                    ></p-button>
                    <p-button
                      label="Buka di Tab Baru"
                      icon="pi pi-external-link"
                      severity="info"
                      size="small"
                      (onClick)="openPdfInNewTab(content.pdf)"
                    ></p-button>
                  </div>
                </div>

                <!-- Alternative: Check if image field contains PDF data -->
                <div *ngIf="isPdfData(content.image)" class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                  <label class="block text-sm font-medium text-blue-800 mb-2">File PDF:</label>
                  <div class="flex items-center gap-2">
                    <i class="pi pi-file-pdf text-red-500"></i>
                    <span class="text-sm text-blue-700">Dokumen PDF (dari field image)</span>
                    <p-button
                      label="Lihat PDF"
                      icon="pi pi-eye"
                      severity="success"
                      size="small"
                      (onClick)="viewPDFBase64(content.image)"
                    ></p-button>
                    <p-button
                      label="Buka di Tab Baru"
                      icon="pi pi-external-link"
                      severity="info"
                      size="small"
                      (onClick)="openPdfInNewTab(content.image)"
                    ></p-button>
                  </div>
                </div>

                <!-- Created Date -->
                <div *ngIf="content.created_at" class="mt-3 text-xs text-gray-500">
                  Dibuat: {{ formatDate(content.created_at) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State for Details -->
          <div *ngIf="loadingDetails" class="flex justify-center items-center py-8">
            <p-progressSpinner [style]="{ width: '50px', height: '50px' }"></p-progressSpinner>
            <span class="ml-3 text-gray-600">Memuat detail...</span>
          </div>

          <!-- No Content Message -->
          <div *ngIf="!loadingDetails && (!anatomyDetails || anatomyDetails.length === 0)" class="text-center py-8">
            <i class="pi pi-info-circle text-3xl text-gray-400 mb-3"></i>
            <p class="text-gray-500">Tidak ada konten yang tersedia</p>
          </div>
        </div>
      </p-dialog>

      <!-- Toast Messages -->
      <p-toast></p-toast>

      <!-- Confirm Dialog -->
      <p-confirmDialog></p-confirmDialog>

      <!-- Image Preview Dialog -->
      <p-dialog
        header="Preview Gambar"
        [(visible)]="showImageDialog"
        [modal]="true"
        [style]="{ width: '80vw', maxWidth: '800px' }"
        [closable]="true"
      >
        <div *ngIf="selectedImage" class="text-center">
          <img
            [src]="selectedImage"
            alt="Preview gambar"
            class="max-w-full h-auto rounded shadow-lg">
        </div>
      </p-dialog>

      <!-- PDF Viewer Dialog -->
      <p-dialog
        header="Viewer PDF"
        [(visible)]="showPdfDialog"
        [modal]="true"
        [style]="{ width: '90vw', height: '85vh' }"
        [maximizable]="true"
        [closable]="true"
        (onHide)="closePdfDialog()"
      >
        <div *ngIf="selectedPdf" class="h-full">
          <pdf-viewer
            [src]="selectedPdf"
            [render-text]="true"
            [original-size]="false"
            [fit-to-page]="true"
            [show-all]="true"
            [page]="1"
            [zoom]="1.0"
            [rotation]="0"
            [show-borders]="true"
            [loading]="'Memuat PDF...'"
            [error]="'Error memuat PDF'"
            style="display: block; width: 100%; height: 75vh; border: 1px solid #ccc;"
            (after-load-complete)="onPdfLoadComplete($event)"
            (error)="onPdfError($event)">
          </pdf-viewer>
        </div>

        <div *ngIf="!selectedPdf" class="text-center py-8">
          <i class="pi pi-file-pdf text-4xl text-gray-400 mb-4"></i>
          <p class="text-gray-500">Tidak ada PDF untuk ditampilkan</p>
        </div>
      </p-dialog>
    </div>
  `,
})
export class ListAnatomi implements OnInit {
  anatomyList: any[] = [];
  selectedAnatomy: any = null;
  anatomyDetails: any[] = [];
  loading: boolean = false;
  loadingDetails: boolean = false;
  showDetailDialog: boolean = false;
  showImageDialog: boolean = false;
  showPdfDialog: boolean = false;
  selectedImage: any = null;
  selectedPdf: any = null;

  constructor(
    private userService: UserService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadAnatomyList();
  }

  async loadAnatomyList() {
    this.loading = true;
    try {
      // Call getAnatomy with proper parameters
      const response = await this.userService.getAnatomy({
        page: 1,
        limit: 100 // Load more data initially
      });

      if (response && response.status) {
        this.anatomyList = response.data || [];
        this.messageService.add({
          severity: 'success',
          summary: 'Berhasil',
          detail: `${this.anatomyList.length} data anatomi dimuat`
        });
      } else {
        this.anatomyList = [];
        this.messageService.add({
          severity: 'warn',
          summary: 'Peringatan',
          detail: response?.message || 'Tidak ada data anatomi yang ditemukan'
        });
      }
    } catch (error) {
      console.error('Error loading anatomy list:', error);
      this.anatomyList = [];
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Gagal memuat data anatomi'
      });
    } finally {
      this.loading = false;
    }
  }

  async viewDetails(anatomy: any) {
    this.selectedAnatomy = anatomy;
    this.showDetailDialog = true;
    this.loadingDetails = true;
    this.anatomyDetails = [];

    try {
      // Use anatomy_id to get details
      const response = await this.userService.getAnatomyDetails({
        anatomy_id: anatomy.id || anatomy.anatomy_id
      });


      if (response && response.status) {
        // The response.data should contain array of anatomy details
        this.anatomyDetails = response.data || [];
      } else {
        this.anatomyDetails = [];
        this.messageService.add({
          severity: 'warn',
          summary: 'Peringatan',
          detail: response?.message || 'Detail anatomi tidak ditemukan'
        });
      }
    } catch (error) {
      console.error('Error loading anatomy details:', error);
      this.anatomyDetails = [];
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Gagal memuat detail anatomi'
      });
    } finally {
      this.loadingDetails = false;
    }
  }

  editAnatomy(anatomy: any) {
    // Navigate to edit form
    this.router.navigate(['/app/osce/anatomi/edit', anatomy.id]);
  }

  deleteAnatomy(anatomy: any) {
    this.confirmationService.confirm({
      message: `Apakah Anda yakin ingin menghapus anatomi "${anatomy.name}"?`,
      header: 'Konfirmasi Hapus',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Hapus',
      rejectLabel: 'Batal',
      acceptIcon: 'pi pi-trash',
      rejectIcon: 'pi pi-times',
      accept: async () => {
        try {
          // Call backend API to delete anatomy
          const response = await this.userService.deleteAnatomy({ id: anatomy.id });

          // Check if deletion was successful
          if (response && response.status) {
            // Show success message
            this.messageService.add({
              severity: 'success',
              summary: 'Berhasil',
              detail: `Anatomi "${anatomy.name}" berhasil dihapus`
            });

            // Reload data from server to ensure consistency
            this.loadAnatomyList();
          } else {
            // Show error message if deletion failed
            this.messageService.add({
              severity: 'error',
              summary: 'Gagal',
              detail: response?.message || 'Gagal menghapus anatomi'
            });
          }
        } catch (error) {
          // Handle error
          console.error('Error deleting anatomy:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Terjadi kesalahan saat menghapus anatomi'
          });
        }
      }
    });
  }

  navigateToCreate() {
    // Navigate to create anatomy page
    this.router.navigate(['/app/osce/anatomi/create']);
  }

  applyGlobalFilter(event: any) {
    const filterValue = (event.target as HTMLInputElement).value;
    // Implement global filter if needed
  }

  viewImage(image: any) {
    this.selectedImage = image;
    this.showImageDialog = true;
  }

  viewImageUrl(imageUrl: string) {
    this.selectedImage = imageUrl;
    this.showImageDialog = true;
  }

  viewImageBase64(base64Image: string) {
    // Assume base64Image already has data URL format or add it
    this.selectedImage = this.getImageSrc(base64Image);
    this.showImageDialog = true;
  }

  viewPDFBase64(base64Pdf: string) {
    try {

      // Clean the base64 string
      let cleanBase64 = base64Pdf;

      // Remove data URL prefix if present
      if (cleanBase64.startsWith('data:')) {
        cleanBase64 = cleanBase64.split(',')[1];
      }

      // Convert base64 to Uint8Array for better PDF viewer compatibility
      const binaryString = atob(cleanBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob and object URL
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Set the PDF source and show dialog
      this.selectedPdf = url;
      this.showPdfDialog = true;

      // Clean up the URL after some time (will be cleaned when dialog closes)
      setTimeout(() => {
        if (url !== this.selectedPdf) {
          URL.revokeObjectURL(url);
        }
      }, 300000); // 5 minutes

    } catch (error) {
      console.error('Error viewing PDF:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Gagal membuka file PDF. Format data mungkin tidak valid.'
      });
    }
  }

  getImageSrc(base64Image: string): string {
    // Check if it's already a data URL
    if (base64Image.startsWith('data:')) {
      return base64Image;
    }
    // Check if it's a regular URL
    if (base64Image.startsWith('http')) {
      return base64Image;
    }
    // Check if it's PDF data (should not be displayed as image)
    if (this.isPdfData(base64Image)) {
      return ''; // Return empty string for PDF data
    }
    // Assume it's base64 string, add data URL prefix
    return `data:image/jpeg;base64,${base64Image}`;
  }

  onImageError(event: any) {
    console.log('Image load error:', event);
    // Hide the image container if image fails to load
    event.target.style.display = 'none';
  }

  getPdfSrc(base64Pdf: string): string {
    try {
      // Clean the base64 string
      let cleanBase64 = base64Pdf;

      // Remove data URL prefix if present
      if (cleanBase64.startsWith('data:')) {
        cleanBase64 = cleanBase64.split(',')[1];
      }

      // Convert to Uint8Array for better compatibility
      const binaryString = atob(cleanBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob and return object URL
      const blob = new Blob([bytes], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error creating PDF source:', error);
      return '';
    }
  }

  onPdfLoadComplete(event: any) {
    console.log('PDF loaded successfully:', event);
  }

  onPdfError(error: any) {
    console.error('PDF load error:', error);
    this.messageService.add({
      severity: 'error',
      summary: 'Error PDF',
      detail: 'Gagal memuat file PDF. File mungkin rusak atau format tidak didukung.'
    });
  }

  closePdfDialog() {
    // Clean up the object URL to prevent memory leaks
    if (this.selectedPdf && this.selectedPdf.startsWith('blob:')) {
      URL.revokeObjectURL(this.selectedPdf);
    }
    this.selectedPdf = null;
    this.showPdfDialog = false;
  }

  openPdfInNewTab(base64Pdf: string) {
    try {
      // Clean the base64 string
      let cleanBase64 = base64Pdf;
      if (cleanBase64.startsWith('data:')) {
        cleanBase64 = cleanBase64.split(',')[1];
      }

      // Convert to Uint8Array
      const binaryString = atob(cleanBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob and open in new tab
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');

      // Clean up after some time
      if (newWindow) {
        setTimeout(() => URL.revokeObjectURL(url), 60000); // 1 minute
      }
    } catch (error) {
      console.error('Error opening PDF in new tab:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Gagal membuka PDF di tab baru'
      });
    }
  }

  isPdfData(base64String: string): boolean {
    if (!base64String) return false;

    try {
      // Check if it's already a PDF data URL
      if (base64String.startsWith('data:application/pdf')) {
        return true;
      }

      // Remove data URL prefix if present
      let cleanBase64 = base64String;
      if (base64String.startsWith('data:')) {
        cleanBase64 = base64String.split(',')[1];
      }

      // Check if the base64 string starts with PDF signature
      // PDF files start with "%PDF-" which in base64 is "JVBERi"
      if (cleanBase64.startsWith('JVBERi')) {
        return true;
      }

      // Try to decode and check for PDF header (first few bytes)
      try {
        const decoded = atob(cleanBase64.substring(0, 20)); // Only decode first 20 chars for efficiency
        return decoded.startsWith('%PDF-');
      } catch (decodeError) {
        // If decode fails, it's probably not valid base64
        return false;
      }
    } catch (error) {
      console.error('Error checking PDF data:', error);
      return false;
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('id-ID', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  }

  viewPDF(pdf: any) {
    try {
      // Convert base64 to blob and open in new tab
      const byteCharacters = atob(pdf.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: pdf.type || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');

      // Clean up the object URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error viewing PDF:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Gagal membuka file PDF'
      });
    }
  }

  getTagSeverity(category: string): string {
    const severityMap: { [key: string]: string } = {
      'Sistem Saraf': 'success',
      'Sistem Kardiovaskular': 'danger',
      'Sistem Respiratori': 'info',
      'Sistem Pencernaan': 'warning',
      'default': 'secondary'
    };

    return severityMap[category] || severityMap['default'];
  }
}
